import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { ArrowRight, Sparkles, Radio, Building2, MessageSquare, PlayCircle, CheckCircle2 } from "lucide-react";

const HERO_IMG = "https://images.unsplash.com/photo-1719825523711-eda3221c111c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzh8MHwxfHNlYXJjaHw0fHxzdXN0YWluYWJsZSUyMHRlY2hub2xvZ3klMjBncmVlbnxlbnwwfHx8fDE3ODYzNzA0MTR8MA&ixlib=rb-4.1.0&q=85";

export default function Landing() {
  const [data, setData] = useState({ proposals: [], videos: [], rooms: [], company_requests: [] });
  const [verified, setVerified] = useState([]);
  const [impact, setImpact] = useState({ total_co2_kg: 0, total_units: 0, count: 0 });

  useEffect(() => {
    api.get("/discover").then((r) => setData(r.data)).catch(() => {});
    api.get("/proposals/verified").then((r) => setVerified(r.data)).catch(() => {});
    api.get("/impact/totals").then((r) => setImpact(r.data)).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen">
      {/* Hero */}
      <section className="relative border-b border-border tick">
        <div className="mx-auto grid max-w-7xl gap-16 px-6 py-24 lg:grid-cols-12 lg:py-32">
          <div className="lg:col-span-7 space-y-8 reveal">
            <div className="inline-flex items-center gap-2 border border-primary/40 px-3 py-1 text-xs uppercase tracking-widest text-primary">
              <span className="h-1.5 w-1.5 rounded-full bg-primary animate-pulse" />
              Citizen Science, Live
            </div>
            <h1 className="text-5xl font-black tracking-tighter sm:text-6xl lg:text-7xl">
              Turn everyday <span className="text-primary">eco-ideas</span> into engineered reality.
            </h1>
            <p className="max-w-xl text-lg leading-relaxed text-muted-foreground">
              A social platform where citizens propose sustainable inventions, an AI industrial-designer refines them
              with materials, costs, and mechanisms, and scientists filter the best to build.
            </p>
            <div className="flex flex-wrap gap-3">
              <Link to="/proposals"><Button size="lg" data-testid="hero-cta-proposals" className="rounded-none bg-primary text-primary-foreground hover:bg-primary/90">Propose an idea <ArrowRight className="ml-2 h-4 w-4" /></Button></Link>
              <Link to="/learn"><Button size="lg" variant="outline" data-testid="hero-cta-learn" className="rounded-none border-border">Watch experts <PlayCircle className="ml-2 h-4 w-4" /></Button></Link>
            </div>
            <div className="grid grid-cols-3 gap-8 pt-8 border-t border-border/60 max-w-lg">
              <Stat n={data.proposals.length} label="Live ideas" />
              <Stat n={data.videos.length} label="Videos" />
              <Stat n={data.rooms.length} label="Chat rooms" />
            </div>
          </div>
          <div className="lg:col-span-5 relative">
            <div className="relative border border-primary/40 overflow-hidden">
              <img src={HERO_IMG} alt="earth" className="h-[420px] w-full object-cover grayscale-[15%]" />
              <div className="absolute inset-0 bg-gradient-to-t from-background via-background/40 to-transparent" />
              <div className="absolute bottom-4 left-4 right-4 border border-primary/60 bg-background/85 p-4">
                <div className="text-[10px] uppercase tracking-widest text-primary">AI Refinement · Live</div>
                <div className="mt-1 font-black text-xl tracking-tight">"A modular vertical mushroom-based insulation panel"</div>
                <div className="mt-2 text-xs text-muted-foreground mono">materials · cost · mechanism · risks · sustainability</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Four pillars */}
      <section className="border-b border-border">
        <div className="mx-auto max-w-7xl px-6 py-20">
          <div className="mb-12 flex items-end justify-between gap-6">
            <h2 className="text-4xl font-black tracking-tighter sm:text-5xl">One world. One echo.</h2>
            <div className="hidden md:block text-xs uppercase tracking-widest text-muted-foreground">/ 01 — 04</div>
          </div>
          <div className="grid gap-px bg-border md:grid-cols-2 lg:grid-cols-4">
            {[
              { icon: Sparkles, k: "01", title: "Proposals", key: "proposals", desc: "Toss a raw idea. Claude Sonnet refines it into a real spec: materials, costs, mechanisms.", to: "/proposals" },
              { icon: Radio, k: "02", title: "Learn", key: "learn", desc: "Long-form deep dives and short-form field clips — exclusively by verified scientists and academics.", to: "/learn" },
              { icon: Building2, k: "03", title: "Company Requests", key: "companies", desc: "Real companies post ecological challenges. The community answers.", to: "/companies" },
              { icon: MessageSquare, k: "04", title: "Chat Rooms", key: "rooms", desc: "Reddit-style rooms segmented by domain — renewables, materials, mobility, waste.", to: "/rooms" },
            ].map((c) => (
              <Link
                key={c.k}
                to={c.to}
                data-testid={`pillar-${c.key}`}
                className="group bg-background p-8 hard-hover"
              >
                <div className="mono text-xs text-primary">{c.k}</div>
                <c.icon className="mt-4 h-6 w-6 text-primary" strokeWidth={2} />
                <h3 className="mt-6 text-2xl font-black tracking-tighter">{c.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{c.desc}</p>
                <div className="mt-6 flex items-center gap-2 text-xs uppercase tracking-widest text-primary opacity-0 transition-opacity group-hover:opacity-100">
                  Enter <ArrowRight className="h-3 w-3" />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Verified success feed */}
      {verified.length > 0 && (
        <section className="border-b border-border bg-primary/5">
          <div className="mx-auto max-w-7xl px-6 py-20">
            <div className="mb-8 flex items-end justify-between gap-4">
              <div>
                <div className="mono text-xs text-primary flex items-center gap-2"><CheckCircle2 className="h-3 w-3" /> verified · real-world impact</div>
                <h2 className="mt-2 text-4xl font-black tracking-tighter">Ideas that shipped.</h2>
                <p className="mt-2 max-w-2xl text-muted-foreground text-sm">Every card below is a citizen's idea that a real company has verified as implemented.</p>
              </div>
              <div className="hidden sm:grid grid-cols-3 gap-6 text-right">
                <StatBig label="Verified" v={impact.count} />
                <StatBig label="CO₂ saved (kg)" v={impact.total_co2_kg} />
                <StatBig label="Units deployed" v={impact.total_units} />
              </div>
            </div>
            <div className="grid gap-px bg-border md:grid-cols-2 lg:grid-cols-3">
              {verified.slice(0, 6).map((p) => (
                <Link key={p.id} to={`/proposals/${p.id}`} data-testid={`verified-card-${p.id}`} className="bg-background p-6 hard-hover">
                  <div className="inline-flex items-center gap-1 border border-primary bg-primary text-primary-foreground px-2 py-0.5 text-[9px] uppercase tracking-widest">
                    <CheckCircle2 className="h-3 w-3" /> Verified Success
                  </div>
                  <h3 className="mt-4 text-xl font-bold tracking-tight line-clamp-2">{p.title}</h3>
                  <p className="mt-2 line-clamp-3 text-sm text-muted-foreground">{p.idea}</p>
                  <div className="mt-4 flex items-center justify-between text-xs">
                    <span className="text-muted-foreground">by {p.author_name}</span>
                    <span className="mono text-primary">via {p.implemented_by_company || p.implemented_by}</span>
                  </div>
                  {(p.co2_saved_kg || p.units_deployed) && (
                    <div className="mt-3 grid grid-cols-2 gap-2">
                      {p.co2_saved_kg != null && <div className="border border-primary/40 p-2"><div className="mono text-primary font-bold text-sm">{p.co2_saved_kg} kg</div><div className="text-[9px] uppercase tracking-widest text-muted-foreground">CO₂/yr</div></div>}
                      {p.units_deployed != null && <div className="border border-primary/40 p-2"><div className="mono text-primary font-bold text-sm">{p.units_deployed}</div><div className="text-[9px] uppercase tracking-widest text-muted-foreground">units</div></div>}
                    </div>
                  )}
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Trending proposals */}
      {data.proposals.length > 0 && (
        <section className="border-b border-border">
          <div className="mx-auto max-w-7xl px-6 py-20">
            <div className="mb-10 flex items-end justify-between">
              <h2 className="text-3xl font-black tracking-tighter">Trending proposals</h2>
              <Link to="/proposals" className="text-xs uppercase tracking-widest text-primary hover:underline">All proposals →</Link>
            </div>
            <div className="grid gap-px bg-border md:grid-cols-2 lg:grid-cols-3">
              {data.proposals.slice(0, 3).map((p) => (
                <Link key={p.id} to={`/proposals/${p.id}`} className="bg-background p-6 hard-hover">
                  <div className="flex items-center justify-between text-xs">
                    <span className="mono text-primary">{p.votes} votes</span>
                    <span className="uppercase tracking-widest text-muted-foreground">{p.status}</span>
                  </div>
                  <h3 className="mt-4 text-xl font-bold tracking-tight line-clamp-2">{p.title}</h3>
                  <p className="mt-2 line-clamp-3 text-sm text-muted-foreground">{p.idea}</p>
                  <div className="mt-4 text-xs text-muted-foreground">by {p.author_name}</div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      <footer className="mx-auto max-w-7xl px-6 py-16">
        <div className="flex flex-wrap items-center justify-between gap-4 border-t border-border pt-8">
          <div className="mono text-xs text-muted-foreground">// eco-chamber.v1 — built for a livable planet</div>
          <div className="text-xs uppercase tracking-widest text-muted-foreground">Propose · Learn · Answer · Discuss</div>
        </div>
      </footer>
    </div>
  );
}

const Stat = ({ n, label }) => (
  <div>
    <div className="text-3xl font-black tracking-tighter text-primary">{n}</div>
    <div className="text-xs uppercase tracking-widest text-muted-foreground">{label}</div>
  </div>
);

const StatBig = ({ v, label }) => (
  <div>
    <div className="mono text-2xl font-black text-primary">{v}</div>
    <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{label}</div>
  </div>
);
